/**
 * TestHealthProfile — colors journey map tasks by `test_status` property.
 *
 * Activates only on @type: process documents whose frontmatter includes
 * @view: journey. Regular BPMN process diagrams are unaffected.
 *
 * See plans/software-design/user-journeys.md § 9 for the full rationale and
 * the sync-from-Playwright pipeline (scripts/sync-test-status-to-journeys.ts).
 *
 * NOTE: this is a minimal scaffold. The actual color overlay logic is
 * implemented in the renderer via the `colorBy` field — this file only
 * declares the profile. Epic B/E fleshes out the color resolver.
 */

import type { DomainDataProfile } from '../../../core/data-profiles';
import type { DiagramType } from '../../../lib/diagram/registries';

export type JourneyNodeType = 'journey_task' | 'journey_gateway' | 'journey_event' | 'journey_subprocess';
export type JourneyEdgeType = 'journey_flow';

export const TEST_HEALTH_PALETTE = {
  passing: '#22C55E',      // green
  failing: '#EF4444',      // red
  flaky: '#F59E0B',        // yellow
  not_covered: '#9CA3AF',  // gray (dashed border added in renderer)
  default: '#E5E7EB',      // faint (null / missing test_case_id)
} as const;

export const TestHealthProfile: DomainDataProfile<JourneyNodeType, JourneyEdgeType> = {
  domainId: 'software-design.journey.test-health',
  profileVersion: '1.0',
  // Journey maps are RDM `@type: process` documents — same diagram type as
  // regular BPMN. The profile activates only when meta.view === 'journey',
  // which the renderer checks before applying the test-status overlay.
  diagramTypes: ['flowchart' as DiagramType],

  nodeTypes: ['journey_task', 'journey_gateway', 'journey_event', 'journey_subprocess'],
  edgeTypes: ['journey_flow'],

  vocabulary: {
    recordSingular: 'Journey task',
    recordPlural: 'Journey tasks',
    repositoryLabel: 'Journey',
    hierarchyLabel: 'Flow',
    importAction: 'Import journey',
    keyColumnHint: 'Task',
    commitLabel: 'Push Journey',
    pullLabel: 'Refresh Journey',
    diagramLabel: 'journey map',
  },

  fieldAliases: {
    test_case_id: ['testCaseId', 'test_case', 'testCase'],
    test_status: ['testStatus', 'status'],
    failure_mode: ['failureMode'],
    metric_id: ['metricId', 'metric'],
  },

  requiredProps: ['stage', 'persona'],
  recommendedProps: ['test_case_id', 'test_status', 'metric_id'],

  primaryShapeType: 'journey_task',

  propsToAttrsMapping: {
    stage: 'stage',
    persona: 'persona',
    test_case_id: 'test_case_id',
    test_status: 'test_status',
    failure_mode: 'failure_mode',
    metric_id: 'metric_id',
    drilldown: 'drilldown',
  },
};
